<template>
  <div class="landing">
    <section class="hero">
      <div class="hero-bg" aria-hidden="true">
        <span class="orb orb-1"></span>
        <span class="orb orb-2"></span>
      </div>
      <div class="hero-inner">
        <header class="hero-header">
          <div class="hero-brand">
            <img class="hero-logo" src="/brand-logo.png" alt="华锐智客云" />
            <span class="hero-name">华锐智客云 CRM</span>
          </div>
        </header>
        <div class="hero-content">
          <p class="hero-eyebrow">面向销售团队的客户经营的免费CRM平台</p>
          <h1 class="hero-title">让客户经营<br />更清晰、更可控、更专业</h1>
          <ul class="hero-scope">
            <li v-for="s in scopes" :key="s">{{ s }}</li>
          </ul>
          <ul class="value-grid">
            <li v-for="v in values" :key="v.title" class="value-item">
              <span class="value-title">{{ v.title }}</span>
              <span class="value-desc">{{ v.desc }}</span>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <section class="auth-panel">
      <div class="auth-card">
        <div class="auth-card-head">
          <h2>{{ headTitle }}</h2>
          <p></p>
        </div>

        <el-form v-if="mode === 'login'" :model="loginForm" :rules="loginRules" ref="loginForm">
          <el-form-item prop="tenantId">
            <el-select
              v-model="loginForm.tenantId"
              filterable
              remote
              clearable
              reserve-keyword
              placeholder="输入企业名称搜索选择"
              prefix-icon="el-icon-office-building"
              style="width:100%"
              :remote-method="searchTenants"
              :loading="tenantSearching"
              @change="onTenantPicked"
            >
              <el-option
                v-for="t in tenantOptions"
                :key="t.id"
                :label="t.tenantName"
                :value="t.id"
              />
            </el-select>
          </el-form-item>
          <el-form-item prop="username">
            <el-input v-model="loginForm.username" placeholder="手机号或用户名" prefix-icon="el-icon-user" />
          </el-form-item>
          <el-form-item prop="password">
            <el-input v-model="loginForm.password" type="password" placeholder="登录密码" prefix-icon="el-icon-lock" show-password @keyup.enter.native="handleLogin" />
          </el-form-item>
          <div class="auth-extra">
            <a class="forgot" href="javascript:;" @click="switchMode('reset')">忘记密码</a>
          </div>
          <el-button type="primary" class="login-btn" :loading="loading" @click="handleLogin">登 录</el-button>
        </el-form>

        <el-form v-else-if="mode === 'register'" :model="regForm" :rules="regRules" ref="regForm">
          <el-form-item prop="tenantName">
            <el-input v-model="regForm.tenantName" placeholder="企业名称" prefix-icon="el-icon-office-building" />
          </el-form-item>
          <el-form-item prop="phone">
            <el-input v-model="regForm.phone" placeholder="11位手机号" maxlength="11" prefix-icon="el-icon-mobile-phone" />
          </el-form-item>
          <el-form-item prop="code">
            <div class="sms-row">
              <el-input v-model="regForm.code" placeholder="短信验证码" maxlength="6" />
              <el-button :disabled="countdown > 0 || sendingSms" :loading="sendingSms" @click="sendCode('register')">
                {{ countdown > 0 ? countdown + 's' : '获取验证码' }}
              </el-button>
            </div>
          </el-form-item>
          <el-form-item prop="password">
            <el-input v-model="regForm.password" type="password" placeholder="设置密码（至少6位，含字母和数字）" show-password prefix-icon="el-icon-lock" />
          </el-form-item>
          <p class="trial-tip">新注册企业默认开通免费版（含 5 个账号，全部功能）</p>
          <el-button type="primary" class="login-btn" :loading="loading" @click="handleRegister">注 册</el-button>
        </el-form>

        <el-form v-else :model="resetForm" :rules="resetRules" ref="resetForm">
          <el-form-item prop="phone">
            <el-input v-model="resetForm.phone" placeholder="已注册手机号" maxlength="11" prefix-icon="el-icon-mobile-phone" />
          </el-form-item>
          <el-form-item prop="code">
            <div class="sms-row">
              <el-input v-model="resetForm.code" placeholder="短信验证码" maxlength="6" />
              <el-button :disabled="countdown > 0 || sendingSms" :loading="sendingSms" @click="sendCode('reset-password')">
                {{ countdown > 0 ? countdown + 's' : '获取验证码' }}
              </el-button>
            </div>
          </el-form-item>
          <el-form-item prop="newPassword">
            <el-input v-model="resetForm.newPassword" type="password" placeholder="新密码（至少6位，含字母和数字）" show-password />
          </el-form-item>
          <el-button type="primary" class="login-btn" :loading="loading" @click="handleReset">重置密码</el-button>
        </el-form>

        <div class="auth-footer">
          <template v-if="mode === 'login'">
            还没有账号？<a href="javascript:;" @click="switchMode('register')">立即注册</a>
          </template>
          <template v-else-if="mode === 'register'">
            已有账号？<a href="javascript:;" @click="switchMode('login')">去登录</a>
          </template>
          <template v-else>
            <a href="javascript:;" @click="switchMode('login')">返回登录</a>
          </template>
        </div>

        <div class="app-dl-box">
          <img v-if="appQrDataUrl" class="app-qr" :src="appQrDataUrl" alt="扫码下载 App" />
          <div class="app-dl-text">
            <div class="app-dl-title">扫码下载 App</div>
            <div class="app-dl-desc">微信扫一扫，按提示用浏览器打开下载</div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { login, register, sendSms, resetPassword, searchLoginTenants } from '../api/auth'
import { PASSWORD_TIP, isValidPassword } from '../utils/password'
import { appDownloadPageUrl } from '../config/appDownload'
import QRCode from 'qrcode'

const LAST_TENANT_KEY = 'lastLoginTenant'

const passwordRule = {
  required: true,
  validator(rule, value, callback) {
    if (!value) return callback(new Error('请设置密码'))
    if (!isValidPassword(value)) return callback(new Error(PASSWORD_TIP))
    callback()
  },
  trigger: 'blur'
}

export default {
  name: 'Login',
  data() {
    return {
      mode: 'login',
      loading: false,
      sendingSms: false,
      countdown: 0,
      timer: null,
      tenantSearching: false,
      tenantOptions: [],
      appQrDataUrl: '',
      scopes: ['线索跟进', '公海领取', '商机推进', '报价合同', '回款审批'],
      values: [
        { title: '线索更清楚', desc: '线索、客户、商机分开管理，转化路径可追踪' },
        { title: '公海可回收', desc: '超时或主动释放的线索和客户进入公海，再分配不丢单' },
        { title: '过程可留痕', desc: '通话、企微、备注和提醒记在同一条生意上' },
        { title: '报价可审批', desc: '报价、合同、回款和审批串成一条成交链路' },
        { title: '权限按角色', desc: '销售、主管、客服看到各自范围内的数据' },
        { title: '字段可扩展', desc: '在系统字段之外增加扩展字段，不改原有字段名' }
      ],
      loginForm: { tenantId: null, username: '', password: '' },
      regForm: { tenantName: '', phone: '', code: '', password: '' },
      resetForm: { phone: '', code: '', newPassword: '' },
      loginRules: {
        tenantId: [{ required: true, message: '请选择企业', trigger: 'change' }],
        username: [{ required: true, message: '请输入账号', trigger: 'blur' }],
        password: [{ required: true, message: '请输入密码', trigger: 'blur' }]
      },
      regRules: {
        tenantName: [{ required: true, message: '请输入企业名称', trigger: 'blur' }],
        phone: [{ required: true, pattern: /^1\d{10}$/, message: '手机号不正确', trigger: 'blur' }],
        code: [{ required: true, message: '请输入验证码', trigger: 'blur' }],
        password: [passwordRule]
      },
      resetRules: {
        phone: [{ required: true, pattern: /^1\d{10}$/, message: '手机号不正确', trigger: 'blur' }],
        code: [{ required: true, message: '请输入验证码', trigger: 'blur' }],
        newPassword: [passwordRule]
      }
    }
  },
  computed: {
    headTitle() {
      if (this.mode === 'register') return '注册企业'
      if (this.mode === 'reset') return '重置密码'
      return '登录华锐智客云'
    },
    headSub() {
      if (this.mode === 'register') return '创建租户并开通免费版'
      if (this.mode === 'reset') return '通过短信验证码重置'
      return '销售团队统一入口'
    }
  },
  created() {
    this.restoreLastTenant()
    this.buildAppQr()
  },
  beforeDestroy() {
    if (this.timer) clearInterval(this.timer)
  },
  methods: {
    async buildAppQr() {
      try {
        this.appQrDataUrl = await QRCode.toDataURL(appDownloadPageUrl(), {
          width: 148,
          margin: 1,
          color: { dark: '#0f172a', light: '#ffffff' }
        })
      } catch (e) {
        this.appQrDataUrl = ''
      }
    },
    restoreLastTenant() {
      try {
        const raw = localStorage.getItem(LAST_TENANT_KEY)
        if (!raw) return
        const t = JSON.parse(raw)
        if (!t || !t.id) return
        this.tenantOptions = [{ id: t.id, tenantName: t.tenantName || String(t.id) }]
        this.loginForm.tenantId = t.id
      } catch (e) { /* ignore */ }
    },
    rememberTenant(id, tenantName) {
      if (!id) return
      const name = tenantName || (this.tenantOptions.find(x => x.id === id) || {}).tenantName || ''
      localStorage.setItem(LAST_TENANT_KEY, JSON.stringify({ id, tenantName: name }))
    },
    rememberTenantFromUser(user) {
      if (!user || !user.tenantId) return
      this.rememberTenant(user.tenantId, user.tenantName)
    },
    onTenantPicked(id) {
      if (!id) return
      const hit = this.tenantOptions.find(x => x.id === id)
      if (hit) this.rememberTenant(hit.id, hit.tenantName)
    },
    async searchTenants(query) {
      const kw = (query || '').trim()
      if (kw.length < 2) {
        const cur = this.loginForm.tenantId
          ? this.tenantOptions.filter(x => x.id === this.loginForm.tenantId)
          : []
        this.tenantOptions = cur
        return
      }
      this.tenantSearching = true
      try {
        const res = await searchLoginTenants(kw, 15)
        const list = res.data || []
        const curId = this.loginForm.tenantId
        const cur = curId ? this.tenantOptions.find(x => x.id === curId) : null
        const map = new Map()
        if (cur) map.set(cur.id, cur)
        list.forEach(t => map.set(t.id, t))
        this.tenantOptions = Array.from(map.values())
      } catch (e) {
        this.tenantOptions = this.loginForm.tenantId
          ? this.tenantOptions.filter(x => x.id === this.loginForm.tenantId)
          : []
      } finally {
        this.tenantSearching = false
      }
    },
    switchMode(mode) {
      this.mode = mode
    },
    startCountdown() {
      this.countdown = 60
      if (this.timer) clearInterval(this.timer)
      this.timer = setInterval(() => {
        this.countdown -= 1
        if (this.countdown <= 0) clearInterval(this.timer)
      }, 1000)
    },
    async sendCode(scene) {
      const phone = scene === 'register' ? this.regForm.phone : this.resetForm.phone
      if (!/^1\d{10}$/.test(phone)) {
        this.$message.warning('请先填写正确手机号')
        return
      }
      this.sendingSms = true
      try {
        const res = await sendSms(phone, scene)
        this.$message.success((res.data && res.data.hint) || '验证码已发送')
        this.startCountdown()
      } finally {
        this.sendingSms = false
      }
    },
    resolveHome() {
      return '/home'
    },
    handleLogin() {
      this.$refs.loginForm.validate(async valid => {
        if (!valid) return
        this.loading = true
        try {
          const res = await login(this.loginForm)
          const user = res.data && res.data.user
          this.rememberTenantFromUser(user)
          this.$store.commit('tagsView/CLEAR_TAGS')
          this.$store.commit('setAuth', res.data)
          const path = await this.resolveHome(user)
          this.$router.replace(path)
        } finally {
          this.loading = false
        }
      })
    },
    handleRegister() {
      this.$refs.regForm.validate(async valid => {
        if (!valid) return
        this.loading = true
        try {
          const res = await register(this.regForm)
          this.rememberTenantFromUser(res.data && res.data.user)
          this.$store.commit('tagsView/CLEAR_TAGS')
          this.$store.commit('setAuth', res.data)
          this.$message.success('注册成功，已开通免费版')
          this.$router.replace('/home')
        } finally {
          this.loading = false
        }
      })
    },
    handleReset() {
      this.$refs.resetForm.validate(async valid => {
        if (!valid) return
        this.loading = true
        try {
          await resetPassword(this.resetForm)
          this.$message.success('密码已重置，请登录')
          this.switchMode('login')
        } finally {
          this.loading = false
        }
      })
    }
  }
}
</script>

<style scoped>
.landing {
  min-height: 100%;
  display: flex;
  background: linear-gradient(135deg, #eff6ff 0%, #f8fafc 45%, #e0f2fe 100%);
  /* 右侧登录标题与左侧口号第二行「更清晰、更高效、更专业」齐平：
     hero 上内边距 36 + 品牌区 52 + 间距 28 + 标签 28 + 间距 14 + 标题首行 44 */
  --auth-align-top: 202px;
}
.hero {
  flex: 1;
  position: relative;
  padding: 36px 48px 40px;
  overflow: hidden;
  display: flex;
  align-items: flex-start;
}
.hero-bg .orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(40px);
  opacity: 0.45;
}
.orb-1 { width: 280px; height: 280px; background: #93c5fd; top: -40px; left: -40px; }
.orb-2 { width: 220px; height: 220px; background: #7dd3fc; bottom: 40px; right: 80px; }
.hero-inner { position: relative; z-index: 1; max-width: 720px; width: 100%; }
.hero-header { margin-bottom: 28px; }
.hero-brand { display: flex; align-items: center; gap: 14px; }
.hero-logo {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  object-fit: contain;
  background: #fff;
  display: block;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.08);
}
.hero-name { font-size: 26px; font-weight: 700; color: #0f172a; letter-spacing: 0.02em; }
.hero-tag { color: #2563eb; font-size: 13px; margin: 0 0 12px; font-weight: 600; }
.hero-eyebrow {
  display: inline-block;
  margin: 0 0 14px;
  padding: 5px 12px;
  border-radius: 999px;
  background: rgba(37, 99, 235, 0.09);
  border: 1px solid rgba(37, 99, 235, 0.16);
  color: #1d4ed8;
  font-size: 13px;
  line-height: 1.2;
  font-weight: 600;
}
.hero-title { font-size: 34px; line-height: 1.3; margin: 0 0 16px; color: #0f172a; font-weight: 800; }
.hero-scope {
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 0 0 22px;
  padding: 0;
}
.hero-scope li {
  padding: 4px 10px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.75);
  border: 1px solid #e2e8f0;
  color: #475569;
  font-size: 12px;
}
.value-grid {
  list-style: none;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin: 0 0 18px;
  padding: 0;
}
.value-item {
  padding: 11px 14px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid #e2e8f0;
}
.value-title {
  display: block;
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 4px;
}
.value-title::before {
  content: '';
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin-right: 7px;
  vertical-align: middle;
  background: linear-gradient(135deg, #2563eb, #0ea5e9);
}
.value-desc { display: block; color: #64748b; font-size: 12px; line-height: 1.6; }
.hero-desc { color: #64748b; font-size: 13px; line-height: 1.7; margin: 0; }
.auth-panel {
  width: 440px; flex-shrink: 0;
  display: flex; flex-direction: column; align-items: center; justify-content: flex-start;
  padding: var(--auth-align-top) 32px 40px; background: rgba(255,255,255,0.72); backdrop-filter: blur(8px);
  border-left: 1px solid #e2e8f0;
}
.auth-card { width: 100%; max-width: 340px; }
.auth-card-head h2 { margin: 0 0 6px; font-size: 22px; line-height: 1.3; color: #0f172a; }
.auth-card-head p { margin: 0 0 24px; color: #64748b; font-size: 13px; }
.login-btn { width: 100%; margin-top: 4px; }
.sms-row { display: flex; gap: 8px; width: 100%; }
.sms-row .el-input { flex: 1; }
.auth-extra { display: flex; justify-content: flex-end; margin: -8px 0 12px; }
.forgot { font-size: 13px; color: #2563eb; text-decoration: none; }
.trial-tip { font-size: 12px; color: #94a3b8; margin: -4px 0 12px; }
.auth-footer { margin-top: 18px; font-size: 13px; color: #64748b; text-align: center; }
.auth-footer a { color: #2563eb; text-decoration: none; }
.app-dl-box {
  margin-top: 28px;
  padding-top: 22px;
  border-top: 1px dashed #e2e8f0;
  display: flex;
  align-items: center;
  gap: 14px;
}
.app-qr {
  width: 96px;
  height: 96px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #fff;
  flex-shrink: 0;
}
.app-dl-text { min-width: 0; text-align: left; }
.app-dl-title { font-size: 14px; font-weight: 700; color: #0f172a; margin-bottom: 4px; }
.app-dl-desc { font-size: 12px; color: #64748b; line-height: 1.55; }
.auth-note { margin-top: 20px; font-size: 12px; color: #94a3b8; }
@media (max-width: 1180px) {
  /* 32 + 52 + 28 + 28 + 14 + 标题首行 36 */
  .landing { --auth-align-top: 190px; }
  .hero { padding: 32px 32px 36px; }
  .hero-title { font-size: 28px; }
  .value-grid { grid-template-columns: minmax(0, 1fr); }
}
@media (max-width: 900px) {
  .landing { flex-direction: column; }
  .hero { padding: 28px 24px; align-items: flex-start; }
  .hero-header { margin-bottom: 20px; }
  .hero-logo { width: 44px; height: 44px; border-radius: 12px; }
  .hero-name { font-size: 22px; }
  .hero-title { font-size: 26px; }
  .value-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .auth-panel {
    width: 100%;
    border-left: none;
    border-top: 1px solid #e2e8f0;
    padding: 32px 24px 40px;
  }
}
@media (max-width: 640px) {
  .value-grid { grid-template-columns: minmax(0, 1fr); }
}
</style>
